import React, { useState } from 'react';
import { getLoginUrl } from '@/utils/domainUtils';
import { ArrowRightCircle, CheckCircle2, Mail, Phone, Building2, Loader2 } from 'lucide-react';

type FormState = 'idle' | 'submitting' | 'success' | 'error';

const CONTACT_ENDPOINT = 'https://us-central1-lemur-476302.cloudfunctions.net/contactSupport';

const GetStartedPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
  });
  const [formState, setFormState] = useState<FormState>('idle');
  const [formError, setFormError] = useState<string | null>(null);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSignUp = () => {
    window.location.href = getLoginUrl('signup=true');
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (formState === 'submitting') return;

    setFormState('submitting');
    setFormError(null);

    try {
      const payload = {
        ...formData,
        message: formData.message || "I'd like to schedule a demo of Complin.ai.",
        source: 'get-started-page',
        pathname: window.location.pathname,
      };

      const response = await fetch(CONTACT_ENDPOINT, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      setFormState('success');
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        message: '',
      });
    } catch (error: any) {
      setFormState('error');
      setFormError(error?.message ? String(error.message) : 'Something went wrong. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-6 py-16">
        <header className="mb-12 text-center">
          <h1 className="text-3xl md:text-4xl font-semibold text-gray-900">Get Started with Complin.ai</h1>
          <p className="mt-4 text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Choose the path that fits your team. Create your account instantly or have our team walk you through a tailored
            demo with your compliance priorities, workflows, and properties.
          </p>
        </header>

        <div className="grid md:grid-cols-2 gap-10 items-start">
          <section className="bg-white shadow-lg rounded-2xl border border-gray-200 p-8 space-y-6">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6 text-primary-600" />
              <h2 className="text-xl font-semibold text-gray-900">Create Your Account</h2>
            </div>
            <p className="text-gray-600 leading-relaxed">
              Start in minutes. Create your organization, invite teammates, and begin rolling out compliance templates,
              reminders, and audit-ready workflows immediately.
            </p>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-center gap-2">
                <ArrowRightCircle className="w-4 h-4 text-primary-600" />
                Unlimited compliances, users, and automated reminders
              </li>
              <li className="flex items-center gap-2">
                <ArrowRightCircle className="w-4 h-4 text-primary-600" />
                Configure properties, vendors, and escalation workflows
              </li>
              <li className="flex items-center gap-2">
                <ArrowRightCircle className="w-4 h-4 text-primary-600" />
                AI-powered obligation extraction and audit vault
              </li>
            </ul>
            <button
              type="button"
              onClick={handleSignUp}
              className="btn-primary w-full mt-4 py-3 text-base font-semibold"
            >
              Sign up now
            </button>
            <p className="text-sm text-gray-500 text-center">
              Looking to log in?{' '}
              <button
                type="button"
                onClick={() => { window.location.href = getLoginUrl(); }}
                className="text-primary-600 hover:text-primary-700 font-medium"
              >
                Go to login
              </button>
            </p>
          </section>

          <section className="bg-white shadow-lg rounded-2xl border border-gray-200 p-8">
            <div className="flex items-center gap-3 mb-6">
              <Mail className="w-6 h-6 text-primary-600" />
              <h2 className="text-xl font-semibold text-gray-900">Request a Demo</h2>
            </div>
            <p className="text-gray-600 leading-relaxed mb-6">
              Share a few details and we&apos;ll schedule a session to map your compliance requirements, migrate existing
              checklists or obligations, and configure automated reminders with your team.
            </p>
            <form className="space-y-5" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Full name
                </label>
                <div className="relative">
                  <input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Your name"
                  />
                  <CheckCircle2 className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2" />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Work email
                </label>
                <div className="relative">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="you@company.com"
                  />
                  <Mail className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
                    Company
                  </label>
                  <div className="relative">
                    <input
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Organization name"
                    />
                    <Building2 className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2" />
                  </div>
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                    Phone (optional)
                  </label>
                  <div className="relative">
                    <input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="+1 (555) 123-4567"
                    />
                    <Phone className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2" />
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                  What do you want to see?
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Tell us about your compliance requirements, locations, or current tooling."
                />
              </div>

              {formState === 'success' && (
                <div className="rounded-lg bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-700">
                  Thanks! We&apos;ll reach out shortly to schedule your demo.
                </div>
              )}

              {formState === 'error' && formError && (
                <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                  {formError}
                </div>
              )}

              <button
                type="submit"
                disabled={formState === 'submitting'}
                className="btn-primary w-full py-3 text-base font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {formState === 'submitting' ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    Book my demo
                    <ArrowRightCircle className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>
          </section>
        </div>
      </div>
    </div>
  );
};

export default GetStartedPage;

